import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Terminal, Shield, Database, Network, Lock, Cpu, HardDrive, Wifi } from 'lucide-react';

const AdvancedBootScreen = ({ onBootComplete }) => {
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('kernel');
  const [showGlitch, setShowGlitch] = useState(false);
  const logsEndRef = useRef(null);

  const scrollToBottom = () => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  const bootLogs = [
    // Kernel Phase
    { phase: 'kernel', text: '[    0.000000] Linux version 6.8.0-cyberbank (root@secure-terminal)', color: 'text-blue-400' },
    { phase: 'kernel', text: '[    0.001024] Command line: BOOT_IMAGE=/boot/vmlinuz-6.8.0', color: 'text-gray-400' },
    { phase: 'kernel', text: '[    0.002048] x86/fpu: Supporting XSAVE feature 0x001: FP', color: 'text-gray-400' },
    { phase: 'kernel', text: '[    0.003072] x86/fpu: Supporting XSAVE feature 0x002: SSE', color: 'text-gray-400' },
    { phase: 'kernel', text: '[    0.004096] Memory: 32GB RAM available', color: 'text-green-400' },
    { phase: 'kernel', text: '[    0.005120] CPU: Intel(R) Xeon(R) Platinum 8375C @ 2.90GHz', color: 'text-gray-400' },
    { phase: 'kernel', text: '[    0.006144] Kernel command line: ro quiet splash', color: 'text-gray-400' },
    
    // Hardware Phase
    { phase: 'hardware', text: '[    0.010240] Detecting hardware...', color: 'text-yellow-400' },
    { phase: 'hardware', text: '[    0.011264] PCI: Using configuration type 1', color: 'text-gray-400' },
    { phase: 'hardware', text: '[    0.012288] Block layer initialized', color: 'text-green-400' },
    { phase: 'hardware', text: '[    0.013312] ACPI: Core revision 20240322', color: 'text-gray-400' },
    { phase: 'hardware', text: '[    0.014336] SCSI subsystem initialized', color: 'text-green-400' },
    { phase: 'hardware', text: '[    0.015360] USB: Core initialized', color: 'text-green-400' },
    
    // Crypto Phase
    { phase: 'crypto', text: '[    0.020480] Initializing cryptographic subsystem...', color: 'text-cyan-400' },
    { phase: 'crypto', text: '[    0.021504] Loading AES-NI encryption module', color: 'text-cyan-400' },
    { phase: 'crypto', text: '[    0.022528] RSA-4096 key generation: SUCCESS', color: 'text-green-400' },
    { phase: 'crypto', text: '[    0.023552] ECC P-521 curve initialized', color: 'text-green-400' },
    { phase: 'crypto', text: '[    0.024576] SHA-512 hash function loaded', color: 'text-green-400' },
    { phase: 'crypto', text: '[    0.025600] Quantum-resistant algorithms: READY', color: 'text-green-400' },
    
    // Database Phase
    { phase: 'database', text: '[    0.030720] Connecting to secure database cluster...', color: 'text-purple-400' },
    { phase: 'database', text: '[    0.031744] PostgreSQL 16.2: Connection established', color: 'text-green-400' },
    { phase: 'database', text: '[    0.032768] SSL/TLS handshake: SUCCESS', color: 'text-green-400' },
    { phase: 'database', text: '[    0.033792] Database schema validation: PASSED', color: 'text-green-400' },
    { phase: 'database', text: '[    0.034816] Transaction log replay: COMPLETE', color: 'text-green-400' },
    
    // Network Phase
    { phase: 'network', text: '[    0.040960] Initializing network stack...', color: 'text-blue-400' },
    { phase: 'network', text: '[    0.041984] IPv4: TCP/IP protocol suite initialized', color: 'text-green-400' },
    { phase: 'network', text: '[    0.043008] IPv6: ADDRCONF(NETDEV_UP): eth0', color: 'text-gray-400' },
    { phase: 'network', text: '[    0.044032] Firewall rules loaded: 2847 chains', color: 'text-green-400' },
    { phase: 'network', text: '[    0.045056] VPN tunnel established: AES-256-GCM', color: 'text-green-400' },
    { phase: 'network', text: '[    0.046080] DNS resolution: OPERATIONAL', color: 'text-green-400' },
    
    // Security Phase
    { phase: 'security', text: '[    0.051200] Loading security modules...', color: 'text-red-400' },
    { phase: 'security', text: '[    0.052224] SELinux: Enforcing mode enabled', color: 'text-green-400' },
    { phase: 'security', text: '[    0.053248] AppArmor: Profile loaded', color: 'text-green-400' },
    { phase: 'security', text: '[    0.054272] Intrusion detection system: ACTIVE', color: 'text-green-400' },
    { phase: 'security', text: '[    0.055296] Biometric scanner: READY', color: 'text-green-400' },
    { phase: 'security', text: '[    0.056320] Multi-factor authentication: ENABLED', color: 'text-green-400' },
    
    // Services Phase
    { phase: 'services', text: '[    0.061440] Starting CYBERBANK TERMINAL services...', color: 'text-green-500' },
    { phase: 'services', text: '[  OK  ] Started Transaction Processing Engine', color: 'text-green-400' },
    { phase: 'services', text: '[  OK  ] Started Real-time Fraud Detection', color: 'text-green-400' },
    { phase: 'services', text: '[  OK  ] Started Blockchain Verification Node', color: 'text-green-400' },
    { phase: 'services', text: '[  OK  ] Started AI Risk Analysis Module', color: 'text-green-400' },
    { phase: 'services', text: '[  OK  ] Started Quantum Encryption Service', color: 'text-green-400' },
    
    // Final Phase
    { phase: 'final', text: '', color: '' },
    { phase: 'final', text: 'CYBERBANK TERMINAL SYSTEM v2.0', color: 'text-terminal-green' },
    { phase: 'final', text: 'SECURE FINANCIAL OPERATIONS NETWORK', color: 'text-terminal-green' },
    { phase: 'final', text: '', color: '' },
    { phase: 'final', text: '[ System Status: OPERATIONAL ]', color: 'text-green-500' },
    { phase: 'final', text: '[ Security Level: MAXIMUM ]', color: 'text-red-500' },
    { phase: 'final', text: '[ Encryption: AES-256-GCM + RSA-4096 ]', color: 'text-cyan-500' },
    { phase: 'final', text: '[ All systems ready for secure transactions ]', color: 'text-green-500' },
  ];

  useEffect(() => {
    let currentIndex = 0;
    let currentPhase = 'kernel';
    
    const addLog = () => {
      if (currentIndex < bootLogs.length) {
        const log = bootLogs[currentIndex];
        
        if (log.phase !== currentPhase) {
          currentPhase = log.phase;
          setPhase(currentPhase);
        }
        
        setLogs(prev => [...prev, log]);
        setProgress((currentIndex / (bootLogs.length - 1)) * 100);
        
        if (Math.random() > 0.95) {
          setShowGlitch(true);
          setTimeout(() => setShowGlitch(false), 50);
        }
        
        currentIndex++;
        const delay = log.phase === 'final' ? 200 : Math.random() * 100 + 20;
        setTimeout(addLog, delay);
      } else {
        setTimeout(() => {
          onBootComplete();
        }, 2000);
      }
    };

    setTimeout(addLog, 500);
  }, []);

  const getPhaseIcon = () => {
    switch(phase) {
      case 'kernel': return Cpu;
      case 'hardware': return HardDrive;
      case 'crypto': return Lock;
      case 'database': return Database;
      case 'network': return Wifi;
      case 'security': return Shield;
      case 'services': return Terminal;
      default: return Terminal;
    }
  };

  const PhaseIcon = getPhaseIcon();

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col overflow-hidden">
      <div className="scanline" />
      
      {/* 1. رأس الصفحة الثابت - اسم النظام */}
      <div className="flex-shrink-0 p-6 border-b border-terminal-green/30 bg-black">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="text-5xl md:text-6xl font-bold cyber-font glow-text mb-2">
            CYBERBANK
          </div>
          <div className="text-sm text-terminal-green-dim">TERMINAL SYSTEM v2.0</div>
        </motion.div>
      </div>

      {/* 2. قسم شريط التقدم والمرحلة - ثابت ولا يتحرك عند التمرير */}
      <div className="flex-shrink-0 p-6 bg-black border-b border-terminal-green/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <PhaseIcon className="w-5 h-5 text-terminal-green animate-pulse" />
            <span className="text-terminal-green text-sm uppercase tracking-wider terminal-font">
              Current Boot Phase: <span className="glow-text">{phase}</span>
            </span>
          </div>
          <div className="h-1.5 bg-gray-900 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-terminal-green"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              style={{ boxShadow: '0 0 10px #00ff41' }}
            />
          </div>
        </div>
      </div>

      {/* 3. منطقة السجلات (Logs) - الجزء الوحيد القابل للتمرير */}
      <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
        {/* إخفاء شريط التمرير برمجياً */}
        <style>{`
          .scrollbar-hide::-webkit-scrollbar { display: none; }
          .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>
        
        <div className="max-w-6xl mx-auto">
          <div className="space-y-1 terminal-font text-xs md:text-sm">
            {logs.map((log, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.1 }}
                className={`${log.color} font-mono leading-tight whitespace-pre-wrap`}
              >
                {log.text}
              </motion.div>
            ))}
            <div ref={logsEndRef} />
          </div>
        </div>
      </div>

      {/* تأثير الخلل (Glitch) */}
      <AnimatePresence>
        {showGlitch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-terminal-green/5 pointer-events-none"
            style={{
              mixBlendMode: 'screen',
              transform: `translate(${Math.random() * 4 - 2}px, ${Math.random() * 4 - 2}px)`
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdvancedBootScreen;